import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, Eye, EyeOff, ShieldCheck } from 'lucide-react';
import { GlassCard, GlassInput, GoldButton } from '../components/common/GlassComponents';
import { toast } from 'sonner';
import { getBackendUrl } from '../utils/urlHelpers';
import { showFriendlyError } from '../utils/errorHandler';

const ResetPasswordPage = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const navigate = useNavigate();

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (!token) {
            toast.error('Invalid or missing reset token.');
            navigate('/auth?mode=login');
        }
    }, [token, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (password !== confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }

        if (password.length < 6) {
            toast.error('Password must be at least 6 characters');
            return;
        }

        setIsLoading(true);

        try {
            const endpoint = getBackendUrl('/api/auth/password-reset/confirm');
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token, new_password: password }),
            });

            const data = await response.json().catch(() => ({}));

            if (!response.ok) {
                throw new Error(data.detail || 'Failed to reset password');
            }

            toast.success('Password successfully reset! You can now log in.');
            navigate('/auth?mode=login');
        } catch (error) {
            showFriendlyError(error, 'Failed to reset password. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4 relative overflow-hidden">
            {/* Background elements */}
            <div className="absolute top-[-10%] left-[-5%] w-96 h-96 bg-gold/20 rounded-full blur-[100px] animate-pulse-slow" />

            <GlassCard className="w-full max-w-md p-8 relative z-10 border-border">
                <div className="text-center mb-8">
                    <div className="flex items-center justify-center gap-3 mb-6">
                        <img src="/assets/images/jride_text_white.png" alt="JRide" className="h-9 w-auto object-contain hidden dark:block" />
                        <img src="/assets/images/jride_text_black.png" alt="JRide" className="h-9 w-auto object-contain block dark:hidden" />
                    </div>
                    <h1 className="text-2xl font-bold text-foreground mb-2 tracking-tight">
                        Create New Password
                    </h1>
                    <p className="text-muted-foreground text-sm">
                        Enter and carefully confirm your new password below.
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="relative">
                        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                        <GlassInput
                            type={showPassword ? 'text' : 'password'}
                            placeholder="New Password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="pl-12 pr-12"
                            required
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                    </div>

                    <div className="relative">
                        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                        <GlassInput
                            type={showPassword ? 'text' : 'password'}
                            placeholder="Confirm New Password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            className="pl-12 pr-12"
                            required
                        />
                    </div>

                    <GoldButton
                        type="submit"
                        isLoading={isLoading}
                        className="w-full py-3 mt-6"
                    >
                        Update Password
                    </GoldButton>
                </form>
            </GlassCard>
        </div>
    );
};

export default ResetPasswordPage;
